import { useRef, useState } from 'react'
import styled from '@emotion/styled'
import Link from 'next/link'
import { getTime } from 'date-fns'

import { HistorySummaryCard } from '../components/SearchSummaryCard'
import { colors, COLORS, PAGE_BREAKPOINTS, ROUTES } from 'parameters/index'
import type { OfferWrapper, Result, SearchWithExtras } from 'generated/api'
import Logger from 'services/Logger'
import useResize from 'lib/hooks/useResize'
import useIsElementInView from 'lib/hooks/useIsElementInView'

import { getResults, getSearches } from 'services/FionaAPI/Search/searchHelpers'
import { SearchCardSkeleton } from 'experiences/search/components/GettingOffers'
import { OfferList } from './OfferList'
import { LoanOffer } from 'components/domain/search/offers/loans/LoanOfferCard/SearchHistoryOfferCard'
import { trackOfferClick } from 'experiences/search/Results/services/analytics'
import { PlusIcon } from 'components/content/icons'
import { LinkButton } from 'ui/inputs/Button'
import { AdvertiserDisclosure } from 'components/content'

const SHOW_DENIED = false

const PageSize = 10

export const SearchHistory = (): JSX.Element => {
	const [selectedSearch, setSelectedSearch] = useState<SearchWithExtras>()

	const [searches, setSearches] = useState<SearchWithExtras[]>([])
	const [results, setResults] = useState<Result>()

	const [loadingSearches, setLoadingSearches] = useState(false)
	const [loadingResults, setLoadingResults] = useState(false)

	const [shouldLoadMore, setShouldLoadMore] = useState(true)

	const page = useRef(0)

	const { width } = useResize()

	const fetchResults = (searchId: string): void => {
		if (!loadingResults && selectedSearch?.id !== searchId) {
			setLoadingResults(true)
			getResults(searchId)
				.then(results => setResults(results))
				.catch(Logger.error)
				.finally(() => setLoadingResults(false))
		}
	}

	const findFirstValidSearch = (searches: SearchWithExtras[]): void => {
		const indexOfFirstValidSearch = searches.findIndex(search => search?.summary?.offerCount || 0)
		if (indexOfFirstValidSearch > -1) {
			setSelectedSearch(searches[indexOfFirstValidSearch])
			fetchResults(searches[indexOfFirstValidSearch].id)
		}
	}

	const fetchSearches = (limit: number, skip: number): void => {
		setLoadingSearches(true)
		getSearches(limit, skip).then(newSearches => {
			setSearches([...searches, ...newSearches])
			if (page.current === 0) {
				findFirstValidSearch(newSearches)
			}
			page.current = page.current + 1
			setLoadingSearches(false)
			if (!newSearches.length) {
				setShouldLoadMore(false)
			}
		})
	}

	const ref = useIsElementInView(() => {
		shouldLoadMore && fetchSearches(PageSize, page.current * PageSize)
	})

	const handleSelectedSearch = (id: string): void => {
		if (!loadingResults) {
			const selectedSearch = searches.find(search => search.id === id)
			setSelectedSearch(selectedSearch)
			fetchResults(id)
			if (width > 1023) {
				window.scroll({
					top: 0,
					left: 0,
					behavior: 'smooth'
				})
			}
		}
	}

	const redirectToOffer = (offer: OfferWrapper): void => {
		trackOfferClick(offer)
		window.location.href = offer.url!
	}

	return (
		<SearchHistoryWrapper>
			<LightPurpleHalfBackground />
			<SearchHistoryLeft>
				<SearchesTop>
					<h2>Searches</h2>
					<ButtonWrapper>
						<LinkButton href={ROUTES.NEW_SEARCH}>
							<PlusIcon /> New
						</LinkButton>
					</ButtonWrapper>
				</SearchesTop>
				{searches
					?.sort((a, b) => getTime(b.created) - getTime(a.created))
					.map((search: SearchWithExtras) => (
						<CardWrapper key={search.id}>
							<HistorySummaryCard
								search={search}
								handleClick={(): void => handleSelectedSearch(search.id)}
								isSelected={search.id === selectedSearch?.id}
							/>
						</CardWrapper>
					))}
				<div ref={ref} style={{ width: '100%', height: '10px' }}></div>
				{loadingSearches ? (
					<SkeletonWrapper>
						<SearchCardSkeleton />
					</SkeletonWrapper>
				) : null}
			</SearchHistoryLeft>
			<SearchHistoryRight>
				<OffersTop>
					<OffersTitle>{selectedSearch?.summary?.intent}</OffersTitle>
					<AdvertiserDisclosure />
				</OffersTop>
				<OffersSubTitle>{selectedSearch?.summary?.products ? selectedSearch.summary.products[0] : ''}</OffersSubTitle>
				<Wrapper>
					<>
						{loadingResults && <LoanOffer pending />}
						{results &&
							!loadingResults &&
							(results.legacyView ? (
								<LegacyOffersWrapper>
									<p style={{ marginBottom: '16px' }}>
										We’ve matched you with quotes from our network of top rated providers.
									</p>
									<LinkButton href={results.legacyView}>View Your Offers</LinkButton>
								</LegacyOffersWrapper>
							) : (
								<>
									<OffersWrapper>
										<OfferList
											redirectToOffer={redirectToOffer}
											providers={results.providers}
											offers={results?.offers
												?.sort(
													(a, b): number =>
														a.relativeDetails?.sortValues?.recScore ||
														0 - (b.relativeDetails?.sortValues?.recScore || 0)
												)
												.slice(0, 4)}
										/>
									</OffersWrapper>
									<Link href="/explore/loans" legacyBehavior>
										<ViewAll>View All {results?.offers?.length || ''} Offers</ViewAll>
									</Link>
								</>
							))}
					</>
				</Wrapper>
				{SHOW_DENIED && (
					<Denied>
						<DeniedTitle>Denied by a lender</DeniedTitle>
						<DeniedText>
							Some lenders don’t think you match their criteria. However, they might contact you with the Letter of
							Adverse Action in the future. It won’t, by any chance, affect your credit score.
						</DeniedText>
						<DeniedLink>I don`t want to receive any mail in the future</DeniedLink>
					</Denied>
				)}
			</SearchHistoryRight>
		</SearchHistoryWrapper>
	)
}

const ButtonWrapper = styled.div`
	button {
		min-width: unset;
	}
`

const SearchesTop = styled.div`
	display: flex;
	justify-content: space-between;
	padding-bottom: 29px;
	width: 100%;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		width: 100%;
		padding-right: 0;
	}
`

const SearchHistoryWrapper = styled.div`
	display: flex;
	position: relative;
	margin-bottom: 35px;
`

const SearchHistoryLeft = styled.div`
	width: 50%;
	padding: 0 24px 0 0;
	@media screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		width: 100%;
		padding-right: 0;
	}
	@media screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		width: 100%;
		padding-right: 0;
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
	}
`

const CardWrapper = styled.div`
	@media screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		width: 31.5%;
	}
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		width: 48.5%;
	}
	@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		width: 100%;
	}
`

const SearchHistoryRight = styled.div`
	width: 50%;
	padding: 0 0 0 24px;
	@media screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		display: none;
	}
`

const OffersTop = styled.div`
	display: flex;
	justify-content: space-between;
	> div {
		padding-top: 5px;
	}
`

const OffersTitle = styled.h2`
	font-size: 24px;
	font-weight: bold;
`

const OffersSubTitle = styled.span`
	font-size: 14px;
	font-weight: normal;
	color: ${COLORS.SPANISH_GRAY};
`

const Wrapper = styled.div`
	padding-top: 21px;
	width: 100%;
	.simpleOfferTitle {
		position: static;
		display: none;
	}
	.simpleOfferText {
		font-size: 14px;
		padding: 0;
	}
	.simpleOfferContent {
		top: 0;
		padding: 0 20px;
		width: 50%;
	}
	.simpleOfferMain {
		padding-top: 37px;
	}
	.simpleOfferImage {
		img {
			width: 100%;
		}
	}
`

const ViewAll = styled.a`
	display: flex;
	padding: 24px;
	align-items: center;
	justify-content: center;
	border-bottom: 1px solid ${COLORS.BORDER_GRAY};
	font-size: 16px;
	color: ${colors.primaryPurple1};
	&:hover {
		color: ${colors.secondaryPurple1};
	}
	text-decoration: underline;
	cursor: pointer;
`

const Denied = styled.div`
	display: flex;
	flex-direction: column;
	padding: 24px 0;
`

const DeniedTitle = styled.h3`
	font-size: 20px;
	margin-bottom: 16px;
`

const DeniedText = styled.span`
	margin-bottom: 16px;
`

const DeniedLink = styled.a`
	color: ${COLORS.MALACHITE_GREEN};
	text-decoration: underline;
	cursor: pointer;
`

const LegacyOffersWrapper = styled.div`
	border-radius: 8px;
	box-shadow: 0 0 20px 0 rgba(69, 69, 69, 0.16);
	background-color: ${COLORS.WHITE};
	padding: 24px;
`

const LightPurpleHalfBackground = styled.div`
	position: fixed;
	background-color: #fcf9ff;
	top: 0;
	right: 0;
	bottom: 0;
	width: 50%;
	z-index: -2;
	@media only screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		display: none;
	}
`

const SkeletonWrapper = styled.div`
	width: 100%;
	padding-left: 75px;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_LANDSCAPE}) {
		padding-left: 0;
	}
`

const OffersWrapper = styled.div`
	> div {
		margin-bottom: 24px;
	}
`
